"use client"

import { useState, useEffect } from "react"
import { Shield, Clock, Info, CheckCircle2, XCircle, Search, RefreshCw } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

interface Boss {
    id: number
    name: string
    level: number
    type: 'Grandboss' | 'Raidboss'
    status: 'Dead' | 'Alive'
    respawnTime: number | null
    serverId: string
}

interface BossData {
    servers: {
        [key: string]: Boss[]
    }
}

export default function BossesPage() {
    const [data, setData] = useState<BossData | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeServer, setActiveServer] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [filter, setFilter] = useState<'all' | 'alive' | 'dead'>('all')

    const fetchBosses = async () => {
        setLoading(true)
        try {
            const res = await fetch("/api/bosses")
            if (res.ok) {
                const json = await res.json()
                setData(json)
                if (!activeServer && Object.keys(json.servers).length > 0) {
                    setActiveServer(Object.keys(json.servers)[0])
                }
            }
        } catch (err) {
            console.error("Failed to fetch bosses")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchBosses()
        // Refresh every minute
        const interval = setInterval(fetchBosses, 60000)
        return () => clearInterval(interval)
    }, [])

    const filteredBosses = data?.servers[activeServer || ""]?.filter(boss => {
        const matchesSearch = boss.name.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesFilter = filter === 'all' ||
            (filter === 'alive' && boss.status === 'Alive') ||
            (filter === 'dead' && boss.status === 'Dead')
        return matchesSearch && matchesFilter
    }) || []

    const formatRespawn = (time: number) => {
        const diff = time - Date.now()
        if (diff <= 0) return "Ready"

        const hours = Math.floor(diff / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

        return `${hours}h ${minutes}m`
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 pt-20 pb-20">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-500 text-[10px] font-black uppercase tracking-[0.2em] mb-6"
                    >
                        <Shield className="w-3 h-3" />
                        Live Status
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl font-black tracking-tighter text-white mb-4 uppercase"
                    >
                        Epic <span className="text-amber-500 text-glow">Bosses</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-slate-400 text-lg max-w-md mx-auto italic"
                    >
                        Track respawn times and status of all major raid bosses.
                    </motion.p>
                </div>

                {/* Controls */}
                <div className="max-w-4xl mx-auto mb-10 space-y-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Server Tabs */}
                        <div className="flex bg-slate-900/50 p-1 rounded-xl border border-slate-800">
                            {data && Object.keys(data.servers).map((server) => (
                                <button
                                    key={server}
                                    onClick={() => setActiveServer(server)}
                                    className={`px-6 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all ${activeServer === server
                                        ? "bg-amber-600 text-white shadow-lg shadow-amber-900/20"
                                        : "text-slate-500 hover:text-slate-300"
                                        }`}
                                >
                                    {server}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative flex-grow">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="text"
                                placeholder="Search boss name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-slate-900/50 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex bg-slate-900/50 p-1 rounded-xl border border-slate-800">
                            {['all', 'alive', 'dead'].map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f as any)}
                                    className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${filter === f
                                        ? "bg-slate-800 text-white"
                                        : "text-slate-500 hover:text-slate-300"
                                        }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* Refresh */}
                        <button
                            onClick={fetchBosses}
                            className={`p-2.5 bg-slate-900/50 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors ${loading ? 'animate-spin' : ''}`}
                        >
                            <RefreshCw className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Boss Grid */}
                <div className="max-w-6xl mx-auto">
                    {loading && !data ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="h-40 bg-slate-900/40 border border-slate-800/50 rounded-2xl animate-pulse" />
                            ))}
                        </div>
                    ) : filteredBosses.length === 0 ? (
                        <div className="text-center py-20 bg-slate-900/20 border border-dashed border-slate-800 rounded-3xl">
                            <Info className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                            <p className="text-slate-500 font-medium">No bosses matching your criteria.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <AnimatePresence mode="popLayout">
                                {filteredBosses.map((boss) => (
                                    <motion.div
                                        layout
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        key={`${boss.serverId}-${boss.type}-${boss.id}`}
                                        className="group relative bg-[#0f172a]/40 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6 hover:border-amber-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/5 overflow-hidden"
                                    >
                                        <div className="relative z-10">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <span className={`text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded ${boss.type === 'Grandboss' ? 'bg-amber-500/20 text-amber-500' : 'bg-blue-500/20 text-blue-400'
                                                        }`}>
                                                        {boss.type}
                                                    </span>
                                                    <h3 className="text-lg font-bold text-white mt-1 group-hover:text-amber-400 transition-colors">
                                                        {boss.name}
                                                    </h3>
                                                    <p className="text-xs text-slate-500 font-bold tracking-widest">LVL {boss.level}</p>
                                                </div>
                                                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${boss.status === 'Alive'
                                                    ? 'bg-green-500/10 text-green-500'
                                                    : 'bg-red-500/10 text-red-500'
                                                    }`}>
                                                    {boss.status === 'Alive' ? (
                                                        <><CheckCircle2 className="w-3 h-3" /> Alive</>
                                                    ) : (
                                                        <><XCircle className="w-3 h-3" /> Dead</>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="mt-6 flex items-center justify-between border-t border-slate-800/50 pt-4">
                                                <div className="flex items-center gap-2 text-slate-400">
                                                    <Clock className="w-4 h-4 text-slate-500" />
                                                    <span className="text-xs font-bold uppercase tracking-widest">Respawn Info</span>
                                                </div>
                                                <span className={`text-sm font-black font-cinzel ${boss.status === 'Alive' ? 'text-green-500/50 italic' : 'text-amber-500'
                                                    }`}>
                                                    {boss.status === 'Alive' ? 'Slaying Required' : (boss.respawnTime ? formatRespawn(boss.respawnTime) : 'Updating...')}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Background Decoration */}
                                        <div className="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-gradient-to-br from-amber-500/10 to-transparent blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                {/* Legend/Info */}
                <div className="max-w-4xl mx-auto mt-20 text-center">
                    <div className="p-8 bg-slate-900/30 border border-slate-800/50 rounded-3xl">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h4 className="text-white font-bold mb-2 font-cinzel tracking-widest uppercase">Auto Update</h4>
                                <p className="text-xs text-slate-500">Boss status is synced automatically every 60 seconds from the game database.</p>
                            </div>
                            <div>
                                <h4 className="text-white font-bold mb-2 font-cinzel tracking-widest uppercase">Grandbosses</h4>
                                <p className="text-xs text-slate-500">Epic world bosses with static location and long respawn windows.</p>
                            </div>
                            <div>
                                <h4 className="text-white font-bold mb-2 font-cinzel tracking-widest uppercase">Raidbosses</h4>
                                <p className="text-xs text-slate-500">Smaller bosses located throughout the world with shorter respawn times.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
